
<template>
  <!-- Authentication Required -->
  <LoginForm v-if="authStore.needsAuth" />
  
  <!-- Main Application -->
  <div v-else id="app" class="min-h-screen bg-slate-50">
    <!-- Header -->
    <header class="bg-white shadow-sm border-b border-gray-200">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16">
          <div class="flex items-center space-x-4">
            <h2 class="text-2xl text-gray-500">
              keim.pe
            </h2>
            <h1 class="text-2xl font-bold text-gray-900">
              IoT Device Management
            </h1>
            <ConnectionIndicator :status="connectionStatus" />
          </div>
          <div class="flex items-center space-x-4">
            <button
              @click="refreshDashboard"
              :disabled="isLoading"
              class="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50"
            >
              ↻ Refresh
            </button>
            
            <!-- User info and logout (when auth is enabled) -->
            <div v-if="authStore.authEnabled && authStore.user" class="flex items-center space-x-3">
              <span class="text-sm text-gray-600">
                👤 {{ authStore.user.username }}
              </span>
              <button
                @click="handleLogout"
                :disabled="authStore.isLoading"
                class="px-3 py-2 text-sm font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
              >
                {{ authStore.isLoading ? 'Logging out...' : 'Logout' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Error Message -->
      <div v-if="error" class="mb-6">
        <div class="bg-red-50 border border-red-200 rounded-md p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800">Error</h3>
              <p class="mt-1 text-sm text-red-700">{{ error }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Dashboard Stats -->
      <DashboardStats 
        :stats="dashboardStats" 
        :loading="isLoading"
        class="mb-8"
      />

      <!-- Navigation Tabs -->
      <div class="mb-6">
        <div class="border-b border-gray-200">
          <nav class="-mb-px flex space-x-8">
            <button
              @click="activeTab = 'devices'"
              :class="[
                'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === 'devices'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              <span class="flex items-center space-x-2">
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>Devices</span>
                <span class="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">{{ deviceList.length }}</span>
              </span>
            </button>
            
            <button
              @click="activeTab = 'rfid-tags'"
              :class="[
                'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === 'rfid-tags'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              <span class="flex items-center space-x-2">
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <span>RFID Tags</span>
                <span class="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">{{ rfidTagStore.tagCount }}</span>
              </span>
            </button>
          </nav>
        </div>
      </div>

      <!-- Tab Content -->
      <div v-if="activeTab === 'devices'" class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Main Area: Device List or Control Interface -->
        <div class="lg:col-span-2">
          <template v-if="selectedDevice && selectedDevice.deviceType === 'ESP32_relationship_hub'">
            <div class="relative bg-white rounded-lg shadow-sm border border-gray-200 h-full flex flex-col">
              <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <button @click="selectedDeviceId = null" class="text-blue-600 hover:underline font-medium flex items-center">
                  <svg class="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
                  Back to device list
                </button>
                <span class="text-lg font-semibold text-gray-900">{{ selectedDevice.deviceId }}</span>
              </div>
              <div class="flex-1 overflow-auto">
                <ESP32RelationshipHubControl
                  :device="selectedDevice"
                  @update-desired="handleUpdateDesired"
                  @sync-device="handleSyncDevice"
                />
              </div>
            </div>
          </template>
          <template v-else>
            <DeviceList
              :devices="deviceList"
              :loading="isLoading"
              @select-device="selectDevice"
              @update-desired="handleUpdateDesired"
              @sync-device="handleSyncDevice"
              @delete-device="handleDeleteDevice"
            />
          </template>
        </div>

        <!-- Device Details/JSON Viewer -->
        <div>
          <DeviceDetails 
            :device="selectedDevice"
            @update-desired="handleUpdateDesired"
            @sync-device="handleSyncDevice"
          />
        </div>
      </div>

      <!-- RFID Tags Tab -->
      <div v-else-if="activeTab === 'rfid-tags'">
        <RFIDTagManagement />
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useDeviceStore } from './stores/deviceStore.js'
import { useAuthStore } from './stores/authStore.js'
import { useRFIDTagStore } from './stores/rfidTagStore.js'

// Components
import LoginForm from './components/LoginForm.vue'
import ConnectionIndicator from './components/ConnectionIndicator.vue'
import DashboardStats from './components/DashboardStats.vue'
import DeviceList from './components/DeviceList.vue'
import DeviceDetails from './components/DeviceDetails.vue'
import ESP32RelationshipHubControl from './components/ESP32RelationshipHub/ESP32RelationshipHubControl.vue'
import RFIDTagManagement from './components/RFIDTagManagement.vue'

// Store
const deviceStore = useDeviceStore()
const authStore = useAuthStore()
const rfidTagStore = useRFIDTagStore()
const { 
  deviceList, 
  connectionStatus, 
  dashboardStats, 
  isLoading, 
  error 
} = storeToRefs(deviceStore)


// Local state: only store selectedDeviceId and active tab
const selectedDeviceId = ref(null)
const activeTab = ref('devices') // 'devices' or 'rfid-tags'

// Always get the latest device object from the store
const selectedDevice = computed(() => {
  if (!selectedDeviceId.value) return null
  return deviceStore.devices.get(selectedDeviceId.value) || null
})

// Methods
const selectDevice = (device) => {
  selectedDeviceId.value = device.deviceId
}

const refreshDashboard = async () => {
  await deviceStore.fetchDevices()
}

const handleUpdateDesired = async (deviceId, desiredState) => {
  try {
    await deviceStore.updateDesiredState(deviceId, desiredState)
    
    // Update selected device if it's the same one
    if (selectedDevice.value?.deviceId === deviceId) {
      selectedDevice.value = deviceStore.devices.get(deviceId)
    }
  } catch (err) {
    console.error('Failed to update desired state:', err)
  }
}

const handleSyncDevice = async (deviceId) => {
  try {
    await deviceStore.triggerDeviceSync(deviceId)
  } catch (err) {
    console.error('Failed to trigger device sync:', err)
  }
}

const handleDeleteDevice = async (deviceId) => {
  if (confirm('Are you sure you want to delete this device?')) {
    try {
      await deviceStore.deleteDevice(deviceId)
      // If the deleted device was selected, clear selection
      if (selectedDeviceId.value === deviceId) {
        selectedDeviceId.value = null
      }
    } catch (err) {
      // Error is already set in store
    }
  }
}

const handleLogout = async () => {
  if (confirm('Are you sure you want to log out?')) {
    // Reset device system first
    resetDeviceSystem()
    // Then logout from auth store
    await authStore.logout()
  }
}

// Initialize device store and data fetching
const initializeDeviceSystem = async () => {
  // Only initialize if authenticated and not already initialized
  if (!authStore.needsAuth && !deviceStore.isInitialized) {
    console.log('🚀 Initializing device system...')
    deviceStore.initializeSocket()
    await refreshDashboard()
    deviceStore.isInitialized = true
  }
}

// Initialize RFID system
const initializeRFIDSystem = async () => {
  if (!authStore.needsAuth) {
    console.log('🏷️ Initializing RFID system...')
    await rfidTagStore.fetchTags()
  }
}

// Reset device system (disconnect and clear data)
const resetDeviceSystem = () => {
  deviceStore.disconnect()
  deviceStore.clearStore()
  rfidTagStore.clearStore()
}

// Watch for authentication changes
watch(
  () => authStore.needsAuth,
  async (needsAuth, previousNeedsAuth) => {
    // If auth was needed but now is not (successful login or auth disabled)
    if (previousNeedsAuth && !needsAuth) {
      console.log('🔐 Authentication successful, initializing app...')
      await initializeDeviceSystem()
      await initializeRFIDSystem()
    }
    // If auth is now needed (logout or session expired)
    if (!previousNeedsAuth && needsAuth) {
      console.log('🔒 Authentication required, resetting app...')
      resetDeviceSystem()
    }
  }
)

// Lifecycle
onMounted(async () => {
  // Always reset device system on mount
  resetDeviceSystem()
  
  // Initialize authentication first and wait for completion
  console.log('🔐 Initializing authentication...')
  await authStore.initializeAuth()
  
  // Wait for auth status to be determined
  await new Promise(resolve => {
    const checkAuth = () => {
      // Check if auth is disabled OR we have a valid authentication state (positive or negative)
      if (!authStore.authEnabled || authStore.isAuthenticated || (!authStore.isAuthenticated && !authStore.isLoading)) {
        resolve()
      } else {
        setTimeout(checkAuth, 50)
      }
    }
    checkAuth()
  })
  
  // Only initialize device system if we don't need auth
  if (!authStore.needsAuth) {
    console.log('✅ No authentication required, initializing systems...')
    await initializeDeviceSystem()
    await initializeRFIDSystem()
  } else {
    console.log('🔒 Authentication required, waiting for login...')
  }
})

onUnmounted(() => {
  // Clean up
  resetDeviceSystem()
})
</script>

<style scoped>
/* Component-specific styles if needed */
</style>